import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import images from "../../images";
import { FormSelect } from "../../components/common/Select/FormSelect";
import {
  useGenQuestQRCodeMutation,
  useGetQuestDetailsQuery,
} from "../../services/apiSlice";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { toast } from "react-toastify";

const QRCode = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const idQuestBusiness = searchParams.get("id_quest_business");

  const [genQuestQRCode, { isLoading: isLoadingGenQuestQRCode }] =
    useGenQuestQRCodeMutation();
  const {
    data: questDetailsData,
    isLoading: isLoadingDetails,
    isError,
  } = useGetQuestDetailsQuery(
    { quest_id: idQuestBusiness },
    { skip: !idQuestBusiness }
  );

  const addressesOptions = (questDetailsData?.data?.quest_locations || []).map(
    (location) => ({
      label: location.location.name || location.location.address || "",
      value: location.location.id || "",
    })
  );

  const [selectedAddress, setSelectedAddress] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  // Memoize genQRCode to stabilize it for useEffect
  const genQRCode = useCallback(async () => {
    if (!selectedAddress || !idQuestBusiness) return;

    try {
      const body = {
        id_quest_business: idQuestBusiness,
        id_location: selectedAddress,
      };
      const response = await genQuestQRCode({ body }).unwrap();

      if (
        response?.message === "Operation successful" ||
        response?.status === 200
      ) {
        const qrCodePath = response?.data?.qr_code_path;
        if (qrCodePath) {
          const fullQrCodeUrl = `${process.env.REACT_APP_URL_IMAGE}/${qrCodePath}`;
          setQrCodeUrl(fullQrCodeUrl);
        } else {
          throw new Error("QR code path not found");
        }
      } else {
        throw new Error("Unexpected response");
      }
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      toast.error(error?.message || t("toast.error.failedToFetchGenQRCodeGen"));
    }
  }, [selectedAddress, idQuestBusiness, genQuestQRCode, t]);

  // Set default address when addressesOptions changes
  useEffect(() => {
    if (addressesOptions.length > 0 && !selectedAddress) {
      setSelectedAddress(addressesOptions[0].value);
    }
  }, [addressesOptions, selectedAddress]);

  // Generate QR code when selectedAddress or genQRCode changes
  useEffect(() => {
    genQRCode();
  }, [selectedAddress, genQRCode]);

  // Handle address selection
  const handleAddressSelect = (event) => {
    setSelectedAddress(event.target.value);
  };

  // Handle download click
  const handleDownloadClick = async (e) => {
    e.preventDefault(); // Prevent default anchor behavior
    if (!qrCodeUrl) {
      toast.error(t("toast.error.noQRCodeAvailableToDownload"));
      return;
    }

    try {
      // Fetch the image as a blob
      const response = await fetch(qrCodeUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch QR code image");
      }
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-code.png"; // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download QR code:", error);
      toast.error(t("toast.error.failedToDownloadQRCode"));
    }
  };

  // Show error if quest details fail to load
  if (isError) {
    return (
      <div className="flex flex-col justify-center items-center mx-auto w-full max-w-[480px] min-h-screen bg-gray-100">
        <div className="text-center text-red-500 py-5">
          {t("toast.error.failedToLoadQuestDetails")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-start items-center mx-auto w-full max-w-[480px] bg-gray-100 min-h-screen relative">
      <LoadingCommon
        statusLoading={isLoadingGenQuestQRCode || isLoadingDetails}
      />
      <div className="fixed top-0 left-0 w-full z-50 flex justify-center items-center">
        <Header
          subTitle={t("qRCode.title")}
          backIconSubtitle={images.ArrowLeftIcon}
          handleBackIconSubtitle={() => navigate(-1)}
          className="justify-end max-w-[480px]"
        />
      </div>
      <div className="h-24" />
      {addressesOptions.length > 0 ? (
        <div className="flex justify-center w-full p-4">
          <FormSelect
            value={selectedAddress}
            onChange={handleAddressSelect}
            options={addressesOptions}
            className="mt-0 bg-stone-100 text-black text-sm font-medium w-full rounded-full py-4 min-w-[400px]"
            disabled={isLoadingGenQuestQRCode || isLoadingDetails}
          />
        </div>
      ) : (
        <div className="text-center text-gray-500 py-5">
          {t("qRCode.noAddressesAvailable")}
        </div>
      )}
      {qrCodeUrl ? (
        <img
          src={qrCodeUrl}
          alt={t("qRCode.generatedQRCode")}
          className="w-full max-w-[220px] mt-8"
        />
      ) : (
        <img
          src={images.QRCodeImage}
          alt={t("qRCode.defaultQRCode")}
          className="w-full max-w-[220px] mt-8"
        />
      )}
      <div className="flex justify-center items-center w-full p-4 mt-8">
        <button
          onClick={handleDownloadClick}
          className={`w-full max-w-[240px] bg-[#3F54DF] text-white rounded-full flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold ${
            !qrCodeUrl ? "opacity-50 cursor-not-allowed" : "hover:bg-indigo-700"
          }`}
          disabled={!qrCodeUrl}
        >
          <img
            src={images.DownloadIcon}
            alt={t("qRCode.downloadIcon")}
            className="w-5 h-5"
          />
          <span>{t("base.download")}</span>
        </button>
      </div>
    </div>
  );
};

export default QRCode;
